import { Translate } from '@teliaee/telia-translate';
import { AxiosError } from 'axios';
import { format } from 'date-fns';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { DatepickerValue, IDateRange, IFormHelperProps } from 'telia-front-react';
import ErrorUtil from '../error/ErrorUtil';
import API from '../util/ApiUtil';
import {
  countryCallingCodeFieldHelper,
  DATE_AND_TIME_ID,
  emailFieldHelper,
  isEmailValid,
  isPhoneValid,
  isValidCallingCode,
  NAME_FIELD_ID,
  notEmptyFieldHelper,
  phoneFieldHelper,
  TOPIC_FIELD_ID
} from '../util/FieldHelperUtil';
import CountryCallingCodeStore, { removePlusAndEstonianAreaCode } from './CountryCallingCodeStore';
import LanguageStore from './LanguageStore';
import SsoStore, { CustomerSegmentType } from './SsoStore';
import {
  AvailableCallbackReservationWrapper,
  CALLBACK_BRANCHES_BY_LANG_TOPIC_SEGMENT,
  CallbackCreation,
  CallbackReservation,
  InitialReservationCreation,
  ReservationCreationError,
  ReservationCreationState
} from '../reservation/common/ReservationInterfaces';
import RootStore from './RootStore';
import ContactInfoStore from './ContactInfoStore';

export const MAX_NAME_FIELD_LENGTH = 254;
export const MAX_DESCRIPTION_FIELD_LENGTH = 300;

class CallbackStore {
  @observable accessor availableReservations = new Map<number, AvailableCallbackReservationWrapper>();
  @observable accessor callbackCreation: CallbackCreation = {
    topic: null,
    description: '',
    subjectContext: 'B2C',
    language: '',
    name: '',
    phone: '',
    email: '',
    branchId: null,
    time: null
  };
  @observable accessor callbackCreationResult: ReservationCreationState = {
    status: 'BEING_FILLED',
    errors: []
  };
  @observable accessor hasCallbackCreationFormSubmitted: boolean = false;

  private contactInfoStore: ContactInfoStore;
  private countryCallingCodeStore: CountryCallingCodeStore;
  private ssoStore: SsoStore;
  private languageStore: LanguageStore;

  constructor(rootStore: RootStore) {
    this.contactInfoStore = rootStore.contactInfoStore;
    this.countryCallingCodeStore = rootStore.countryCallingCodeStore;
    this.ssoStore = rootStore.ssoStore;
    this.languageStore = rootStore.languageStore;
    makeObservable(this);

    this.callbackCreation.language = this.languageStore.currentLanguage;
    this.setLanguageBasedOnWebsiteLanguage();
  }

  @computed
  private get customerType(): CustomerSegmentType {
    return this.ssoStore.customerSegmentType || this.callbackCreation.subjectContext;
  }

  private setLanguageBasedOnWebsiteLanguage() {
    reaction(
      () => this.languageStore.currentLanguage,
      async (language) => {
        runInAction(() => (this.callbackCreation.language = language));
      }
    );
  }

  @action.bound
  public updateReservationCreationBranchId() {
    this.callbackCreation.branchId =
      this.callbackCreation &&
      this.callbackCreation.language &&
      this.callbackCreation.topic &&
      this.callbackCreation.subjectContext &&
      CALLBACK_BRANCHES_BY_LANG_TOPIC_SEGMENT[this.callbackCreation.language.toLowerCase()][this.callbackCreation.topic][this.callbackCreation.subjectContext];

    if (this.callbackCreation.branchId) {
      this.fetchAvailableReservations(this.callbackCreation.branchId);
    }
    this.callbackCreation.time = null;
  }

  @computed
  public get hasAvailableReservations(): boolean {
    const branchId = this.callbackCreation.branchId;

    return !!(branchId && this.getAvailableReservationsForBranch(branchId)?.length);
  }

  @action.bound
  public async fetchAvailableReservations(branchId: number) {
    this.availableReservations.set(branchId, { status: 'FETCHING' });
    try {
      const { data } = await API.get('/reservation/available', { params: { branchId } });
      runInAction(() => {
        this.availableReservations.set(branchId, {
          availableReservations: data,
          status: 'FETCHED'
        });
      });
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
    }
  }

  @computed
  public get selectedBranchTimesFetched(): boolean {
    return this.callbackCreation && this.availableReservations.get(this.callbackCreation.branchId)?.status === 'FETCHED';
  }

  public getAvailableReservationsForBranch = (branchId: number) => {
    const branchData = this.availableReservations.get(branchId);
    return branchData?.availableReservations || [];
  };

  public isAvailableReservationsFetching(branchId: number): boolean {
    const branchData = this.availableReservations.get(branchId);

    return !!branchData && ['UNINITIALIZED', 'FETCHING'].includes(branchData.status);
  }

  @action.bound
  public async createReservation(branchId: number, reservation: CallbackReservation) {
    try {
      this.callbackCreationResult.status = 'SENDING';

      const updatedReservation: CallbackReservation = {
        ...reservation,
        phone: `${this.countryCallingCodeStore.countryCallingCode}${reservation.phone}`,
        email: this.countryCallingCodeStore.isForeignCodeSelected && !!reservation.email ? reservation.email : ''
      };
      await API.post(`/reservation/create/${branchId}`, updatedReservation);
      runInAction(() => (this.callbackCreationResult.status = 'SUCCESS'));
    } catch (e) {
      await this.handleReservationCreationError(e as AxiosError);
    }
  }

  @action.bound
  private handleReservationCreationError(e: AxiosError): void {
    if (e.response.status === ReservationCreationError.BAD_REQUEST) {
      this.callbackCreationResult.status = 'INVALID_INPUT';
      this.callbackCreationResult.errors = (e.response.data as any).errors;
      this.fetchAvailableReservations(this.callbackCreation.branchId);
    } else {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      this.callbackCreationResult.status = 'ERROR';
    }
  }

  @action.bound
  public selectTime(time: DatepickerValue): void {
    this.callbackCreation.time = time ? format((time as IDateRange).from, "yyyy-MM-dd'T'HH:mm") : null;
  }

  @computed
  public get dateAndTimeSelectionValid(): boolean {
    return this.callbackCreation.branchId && this.getAvailableReservationsForBranch(this.callbackCreation.branchId).length > 0;
  }

  @computed
  public get noDatesAvailable(): boolean {
    const branchId = this.callbackCreation.branchId;

    if (branchId && !this.isAvailableReservationsFetching(branchId)) {
      const branchReservations = this.getAvailableReservationsForBranch(branchId);
      return branchReservations && branchReservations.length === 0;
    }

    return false;
  }

  @action.bound
  public validateFieldsAndShowPreview(): void {
    this.hasCallbackCreationFormSubmitted = true;
    this.callbackCreationResult.status = this.requiredFieldsAreValid ? 'PREVIEW' : 'VALIDATION_FAILED';
  }

  @computed
  public get requiredFieldsAreValid(): boolean {
    const { countryCallingCode, isForeignCodeSelected } = this.countryCallingCodeStore;
    const emailIsValid = isForeignCodeSelected ? !!this.callbackCreation.email && isEmailValid(this.callbackCreation.email) : true;

    return (
      !!this.callbackCreation.topic &&
      this.callbackCreation.time &&
      this.callbackCreation.phone &&
      isPhoneValid(this.callbackCreation.phone.replace('+', '')) &&
      countryCallingCode &&
      isValidCallingCode(countryCallingCode) &&
      !!this.callbackCreation.name &&
      emailIsValid
    );
  }

  public async prefillReservationCreation(creation: InitialReservationCreation) {
    await this.initContactInfoIfNecessary(creation.customerId);
    await this.fetchCommunicationLanguageIfNecessary(creation.communicationLanguageCode);
    const phoneNumber = creation.phone || this.contactInfoStore.phone;

    runInAction(
      () =>
        (this.callbackCreation = {
          ...this.callbackCreation,
          phone: phoneNumber ? removePlusAndEstonianAreaCode(phoneNumber) : null,
          email: this.contactInfoStore.email || '',
          name: creation.name?.slice(0, MAX_NAME_FIELD_LENGTH),
          customerId: creation.customerId,
          subjectContext: creation.segment || this.customerType,
          language: creation.communicationLanguageCode?.toLowerCase() || this.contactInfoStore.communicationLanguageCode || this.languageStore.currentLanguage
        })
    );
  }

  private async initContactInfoIfNecessary(customerId: number) {
    const customerIdMatchesSSO = !!customerId && this.ssoStore.customerId === customerId;

    if (customerIdMatchesSSO) {
      await this.contactInfoStore.fetchContactInfo(customerId);
    }
  }

  private async fetchCommunicationLanguageIfNecessary(communicationLanguageCode: string) {
    if (!communicationLanguageCode && !!this.ssoStore.customerId) {
      await this.contactInfoStore.fetchUserInfo();
    }
  }

  public getPhoneHelper = (translate: Translate): IFormHelperProps =>
    phoneFieldHelper(translate, this.callbackCreation.phone, this.hasCallbackCreationFormSubmitted);

  public getEmailHelper = (translate: Translate): IFormHelperProps =>
    emailFieldHelper(translate, this.callbackCreation.email, this.hasCallbackCreationFormSubmitted);

  public getNameHelper = (translate: Translate): IFormHelperProps =>
    notEmptyFieldHelper(translate, this.callbackCreation.name, this.hasCallbackCreationFormSubmitted, NAME_FIELD_ID);

  public getCallingCodeHelper = (translate: Translate): IFormHelperProps =>
    countryCallingCodeFieldHelper(translate, this.countryCallingCodeStore.countryCallingCode, this.hasCallbackCreationFormSubmitted);

  public getDateAndTimeHelper = (translate: Translate): IFormHelperProps =>
    notEmptyFieldHelper(translate, this.callbackCreation.time, this.hasCallbackCreationFormSubmitted, DATE_AND_TIME_ID);

  public getTopicHelper = (translate: Translate): IFormHelperProps =>
    notEmptyFieldHelper(translate, this.callbackCreation.topic, this.hasCallbackCreationFormSubmitted, TOPIC_FIELD_ID);
}

export default CallbackStore;
